/**
 * Front-camera sensing — the reflection-mode layer (TECH_PLAN §1).
 *
 * ETHICS (spine §9): sensing is OPTIONAL, skippable, and episodic. It runs fully
 * on-device via MediaPipe FaceLandmarker. **Only low-dimensional features leave
 * this module** — never the video, never landmarks, never the image. We expose
 * valence/arousal/attention proxies derived from blendshapes, and nothing else.
 *
 * MediaPipe is dynamically imported so the core work has no dependency on it and
 * the main bundle stays small. If loading or inference fails, the field degrades
 * to orientation-only with no loss of function.
 */

import { FACE_MODEL, MEDIAPIPE_WASM } from '../config.js';

/** The only data this module emits. Deliberately low-dimensional. */
export interface AffectFeatures {
  /** -1 (negative) .. +1 (positive), from smile vs frown/brow blendshapes. */
  valence: number;
  /** 0 (calm) .. 1 (aroused), from eye-widen / jaw / brow activation. */
  arousal: number;
  /** 0 (looking away) .. 1 (attending), from face presence + gaze-ish proxy. */
  attention: number;
  /** true only while a face is detected this frame. */
  present: boolean;
}

const NEUTRAL: AffectFeatures = { valence: 0, arousal: 0, attention: 0, present: false };

type Landmarker = {
  detectForVideo: (video: HTMLVideoElement, ts: number) => {
    faceBlendshapes?: { categories: { categoryName: string; score: number }[] }[];
  };
  close: () => void;
};

export class FaceSensor {
  private landmarker: Landmarker | null = null;
  private loading: Promise<void> | null = null;
  private lastTs = -1;

  /** Lazily load MediaPipe + the model. Safe to call repeatedly. */
  async load(): Promise<void> {
    if (this.landmarker) return;
    if (this.loading) return this.loading;
    this.loading = (async () => {
      const vision = await import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM);
      this.landmarker = (await vision.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: FACE_MODEL, delegate: 'GPU' },
        runningMode: 'VIDEO',
        outputFaceBlendshapes: true,
        numFaces: 1,
      })) as unknown as Landmarker;
    })();
    return this.loading;
  }

  /** Run one inference on the current video frame. Returns low-dim features only. */
  detect(video: HTMLVideoElement, now = performance.now()): AffectFeatures {
    if (!this.landmarker || video.readyState < 2) return NEUTRAL;
    // MediaPipe requires strictly increasing timestamps.
    const ts = now <= this.lastTs ? this.lastTs + 1 : now;
    this.lastTs = ts;

    let res;
    try {
      res = this.landmarker.detectForVideo(video, ts);
    } catch {
      return NEUTRAL;
    }
    const shapes = res.faceBlendshapes?.[0]?.categories;
    if (!shapes || shapes.length === 0) return NEUTRAL;

    const get = (name: string) => shapes.find((c) => c.categoryName === name)?.score ?? 0;

    const smile = (get('mouthSmileLeft') + get('mouthSmileRight')) / 2;
    const frown = (get('mouthFrownLeft') + get('mouthFrownRight')) / 2;
    const browDown = (get('browDownLeft') + get('browDownRight')) / 2;
    const browUp = get('browInnerUp');
    const eyeWide = (get('eyeWideLeft') + get('eyeWideRight')) / 2;
    const jawOpen = get('jawOpen');
    const blinkL = get('eyeBlinkLeft');
    const blinkR = get('eyeBlinkRight');

    const valence = clamp(smile - frown - browDown * 0.5, -1, 1);
    const arousal = clamp((eyeWide + jawOpen + browUp) / 2, 0, 1);
    // crude attention proxy: eyes open (not blinking) and face present
    const attention = clamp(1 - (blinkL + blinkR) / 2, 0, 1);

    return { valence, arousal, attention, present: true };
  }

  close(): void {
    this.landmarker?.close();
    this.landmarker = null;
    this.loading = null;
  }
}

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
